/* eslint-disable camelcase */
/* eslint-disable react/jsx-props-no-spreading */
import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import {
  ClickAwayListener, Grow, MenuItem, MenuList, Paper, Popper,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import RecordDelete, { getRef } from '../../record-viewer/record-delete';
import { SystemAppData } from '../../hooks/use-system-app';

interface Props {
  record: Record<string, any>;
  formData: SystemAppData;
  callBack: (id?: string) => void;
}

const TopBarMenu: React.FC<Props> = ({ record, formData, callBack }: Props) => {
  const [open, setOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState({ id: '', name: '', rrule: null });
  const nameDataRef = useMemo(() => getRef(formData?.restForm), [formData]);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const { system_type } = formData?.restForm;
  const recordType = system_type.split('_')[1];
  const deleteRecordCopy = `Delete ${recordType}`;
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (
      anchorRef.current
      && anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }
    setOpen(false);
  };

  const handleListKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Tab') {
      event.preventDefault();
      setOpen(false);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  };

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = React.useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current!.focus();
    }

    prevOpen.current = open;
  }, [open]);

  const handleDeleteRecord = (e) => {
    e.stopPropagation();
    setRecordToDelete({ id: record?.id, rrule: record?.rrule, name: record?.form_values[nameDataRef ?? ''] });
  };

  const handleAfterDeteletRecord = (id?: string) => {
    setRecordToDelete({ ...recordToDelete, id: '', rrule: null });
    callBack(id);
    setOpen(false);
  };

  return (
    <div>
      <RecordDelete
        restForm={formData?.restForm}
        recordToDelete={recordToDelete}
        callback={handleAfterDeteletRecord}
      />
      <IconButton
        ref={anchorRef}
        id="composition-button"
        aria-controls={open ? 'composition-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <MoreHorizIcon />
      </IconButton>
      <Popper
        sx={{ zIndex: 100 }}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        placement="bottom-start"
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
              placement === 'bottom-start' ? 'left top' : 'left bottom',
            }}
          >
            <Paper data-testid="paper-test" sx={{ marginTop: '4px' }} elevation={4}>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  data-testid="menu-list"
                  sx={{ padding: 0 }}
                  autoFocusItem={open}
                  id="composition-menu"
                  aria-labelledby="composition-button"
                  onKeyDown={handleListKeyDown}
                >
                  <MenuItem
                    sx={{ padding: '8px 38px 8px 16px' }}
                    onClick={handleDeleteRecord}
                  >
                    {deleteRecordCopy}
                  </MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </div>
  );
};

export default TopBarMenu;
